import "./App.css";

import i18n from "i18next";
import _ from "lodash";
import React from "react";

import {
  TDExportBackground,
  TDExportType,
  TDShape,
  TDShapeType,
  Tldraw,
  TldrawApp,
} from "@psi/tldraw";

import { DarkModeButton } from "./DarkModeButton";
import { TldrawContext } from "./hooks/useTldrawApp";
import { isSupportedLanguage, toTldrawLanguage } from "./localization";
import {
  DrawingMessage,
  EventMessage,
  isSetStateMessage,
  parseMessageBody,
  sendMessage,
} from "./messaging";
import { getLastState, setLastState } from "./state";
import { blobToBase64, debounce, getDiff } from "./utils";
import { useTranslation } from "react-i18next";

const queryParams = new URLSearchParams(window.location.search);

function setLocale(app: TldrawApp) {
  if (queryParams.has("locale")) {
    const locale = queryParams.get("locale") || "en";
    if (isSupportedLanguage(locale)) {
      const dir = i18n.dir(i18n.language);
      document.documentElement.dir = dir;
      console.debug("elements text direction:", dir);
      i18n.changeLanguage(locale);
      const tlDrawLocale = toTldrawLanguage(locale);
      app.setSetting("language", tlDrawLocale);
      console.debug("set locale", locale, tlDrawLocale);
    } else {
      console.warn("locale not supported, using default: " + locale);
    }
  }
}

async function exportImage(app: TldrawApp | undefined, shapeIds: string[]): Promise<string> {
  if (!app) {
    return "";
  }

  const blob = await app.getImage(TDExportType.JPG, {
    scale: 1,
    quality: 0.5,
    transparentBackground: false,
    ids: shapeIds,
  });

  if (!blob) {
    return "";
  }

  const base64 = await blobToBase64(blob);
  return base64;
}

function getShapes(app: TldrawApp): TDShape[] {
  if (!app) {
    return [];
  }

  const shapeIds = Object.keys(app.page.shapes);
  const shapes = shapeIds.map((id) => app.getShape(id));
  return shapes;
}

async function sendExportMessage(app: TldrawApp | undefined) {
  if (!app) {
    return;
  }

  try {
    const shapes = getShapes(app);
    const shapeIds = shapes.map((shape) => shape.id);
    const diff = getDiff(shapes, getLastState(), (a, b) => a.id === b.id && _.isEqual(a, b));

    if (diff.length === 0) {
      return;
    }

    setLastState([...shapes]);

    const serialized = JSON.stringify(shapes);

    const base64 = await exportImage(app, shapeIds);

    const msg: EventMessage<DrawingMessage> = {
      event: "psi-export",
      data: {
        shapes: serialized,
        jpegB64: base64,
      },
    };

    sendMessage(msg);
  } catch (e: any) {
    console.error(e);
    const msg: EventMessage<string> = {
      data: e.toString(),
      event: "psi-error",
    };
    sendMessage(msg);
  }
}

// mounted happens multiple times at once, debounce it
const sendMounted = debounce(() => {
  const msg: EventMessage<void> = {
    event: "psi-mounted",
    data: undefined,
  };
  sendMessage(msg);
});

function App() {
  const { t } = useTranslation();

  const [app, setApp] = React.useState<TldrawApp>(new TldrawApp());

  React.useEffect(() => {
    const handleWindowMessage = (msg: MessageEvent<any>) => {
      try {
        const evtMsg = parseMessageBody<EventMessage<DrawingMessage>>(msg);
        if (!isSetStateMessage(evtMsg)) {
          return;
        }

        const shapes = JSON.parse(evtMsg.data.shapes);
        app?.create(shapes);
        const ogType = app.currentTool.type;
        app?.selectNone();

        // due to weird behavior with select none reset the tool to whatever had already been chosen.
        app.selectTool(ogType === TDShapeType.Rectangle ? TDShapeType.Text : TDShapeType.Rectangle);
        app.selectTool(ogType);
      } catch (e: any) {
        console.error(e);
        const msg: EventMessage<string> = {
          data: e.toString(),
          event: "psi-error",
        };
        sendMessage(msg);
      }
    };

    window.addEventListener("message", handleWindowMessage);

    // cleanup this component
    return () => {
      window.removeEventListener("message", handleWindowMessage);
    };
  }, [app]);

  const handleMount = React.useCallback(
    (app: TldrawApp) => {
      app.setSetting("exportBackground", TDExportBackground.Auto);

      // app.setSetting("keepStyleMenuOpen", true); // Use this to show the style menu by default
      app.selectTool(TDShapeType.Text);
      setApp(app);
      setLocale(app);
      sendMounted();

      document.title = t("DOCUMENT_TITLE");
    },
    [t],
  );

  const handleChange = React.useMemo(
    () =>
      debounce((app: TldrawApp, reason?: string) => {
        sendExportMessage(app);
      }),
    [],
  );

  return (
    <TldrawContext.Provider value={app}>
      <div className="App">
        <div className="tldraw">
          <Tldraw
            showPages={false}
            disableAssets={true}
            showMultiplayerMenu={false}
            showMenu={false}
            onMount={handleMount}
            onChange={handleChange}
          />
        </div>
        <div className="dark-mode-container">
          <DarkModeButton></DarkModeButton>
        </div>
      </div>
    </TldrawContext.Provider>
  );
}

export default App;
